<template>
  <BaseModal
    :title="$i18next.t('message.ai.dubbings.modal.description')"
    hookSufix="SubtitleModal"
    :disableDefaultPadding="disableDefaultPadding"
    :onCloseHook="onCloseHook"
    :onOpenHook="onOpenHook"
  >
    <template #content>
      <v-container class="pa-0">
        <template v-if="currentModalStage.globalStage === 'initial'">
          <DefaultInitialState
            :subtitle="$i18next.t('message.ai.subtitles.modal.description')"
            :cardData="initialStageCardsData"
          />
        </template>
        <template v-if="currentModalStage.globalStage === 'generate'">
          <StepStage
            :loadings="loadings"
            :langCodes="langCodes"
            :unusedLangCodes="unusedLangCodes"
            :subStage="currentModalStage.subStage"
            @changeSubStage="updateSubStage"
            :totalSteps="2"
          >
            <template #step_1>
              <p
                class="text-h7 mt-8 mb-11 text-center"
                :class="{ 'neutral--text': !$vuetify.theme.dark }"
              >
                {{
                  $i18next.t(
                    'message.ai.subtitles.modal.auto.step_one.description'
                  )
                }}
              </p>
              <div class="d-flex flex-column container__setup-action">
                <template v-if="!subtitles.length">
                  <v-radio-group
                    v-model="generateSubtitleData.subtitleFromAiOption"
                    hide-details
                    @change="generateSubtitleData.subtitleFromAiOption = 0"
                    class="mt-0 pt-0 radio__ai"
                  >
                    <v-radio :value="0">
                      <template #label>
                        <span
                          :class="{ 'neutral--text': !$vuetify.theme.dark }"
                        >
                          {{
                            $i18next.t(
                              'message.ai.subtitles.modal.auto.step_one.options.transcription.label'
                            )
                          }}
                        </span>
                      </template>
                    </v-radio>
                  </v-radio-group>
                  <div
                    :class="{
                      'not-allowed':
                        generateSubtitleData.subtitleFromAiOption !== 0,
                    }"
                  >
                    <v-select-legacy
                      v-model="generateSubtitleData.subtitleSrcLangFromVideo"
                      :disabled="
                        generateSubtitleData.subtitleFromAiOption !== 0
                      "
                      class="v-input__custom-colorized ml-9 align-self-end mt-1"
                      style="width: 315px"
                      outlined
                      hide-details
                      :placeholder="
                        $i18next.t(
                          'message.ai.subtitles.modal.auto.step_one.options.transcription.placeholder'
                        )
                      "
                      item-text="label"
                      item-value="srclang"
                      :value="generateSubtitleData.subtitleSrcLangFromVideo"
                      :menu-props="{ top: false, offsetY: true }"
                      :class="{
                        dark: $vuetify.theme.dark,
                      }"
                      :no-data-text="
                        $t('message.pages.videoEditModal.usedSubtitles')
                      "
                      :items="availableTranscriptionLangCodes"
                      dense
                    >
                      <template
                        #prepend-inner
                        v-if="generateSubtitleData.subtitleSrcLangFromVideo"
                      >
                        <country-flag
                          :country="
                            getCountryCodeBySrcLang(
                              getPreparedSrcLang(
                                generateSubtitleData.subtitleSrcLangFromVideo
                              )
                            )
                          "
                        />
                      </template>
                      <template #append>
                        <v-icon> mdi-chevron-down </v-icon>
                      </template>
                    </v-select-legacy>
                  </div>
                </template>
                <div
                  :class="{ 'mt-6': !subtitles.length }"
                  v-if="subtitles.length"
                >
                  <v-radio-group
                    v-model="generateSubtitleData.subtitleFromAiOption"
                    hide-details
                    @change="generateSubtitleData.subtitleFromAiOption = 1"
                    class="mt-0 pt-0 radio__ai"
                  >
                    <v-radio :value="1">
                      <template #label>
                        <span
                          :class="{ 'neutral--text': !$vuetify.theme.dark }"
                        >
                          {{
                            $i18next.t(
                              'message.ai.subtitles.modal.auto.step_one.options.translation.label'
                            )
                          }}
                        </span>
                      </template>
                    </v-radio>
                  </v-radio-group>
                  <div
                    style="width: 315px"
                    class="align-self-end"
                    :class="{
                      'not-allowed':
                        generateSubtitleData.subtitleFromAiOption !== 1,
                    }"
                  >
                    <v-select-legacy
                      :disabled="
                        generateSubtitleData.subtitleFromAiOption !== 1
                      "
                      v-model="
                        generateSubtitleData.subtitleLabelFromAnotherExistingOne
                      "
                      class="v-input__custom-colorized ml-9 mt-1"
                      outlined
                      hide-details
                      :placeholder="
                        $i18next.t(
                          'message.ai.subtitles.modal.auto.step_one.options.translation.placeholder'
                        )
                      "
                      item-text="label"
                      item-value="srclang"
                      :value="
                        generateSubtitleData.subtitleLabelFromAnotherExistingOne
                      "
                      :menu-props="{ top: false, offsetY: true }"
                      :class="{
                        dark: $vuetify.theme.dark,
                      }"
                      :no-data-text="
                        $t('message.pages.videoEditModal.usedSubtitles')
                      "
                      :items="subtitles"
                      dense
                    >
                      <template
                        #prepend-inner
                        v-if="
                          generateSubtitleData.subtitleLabelFromAnotherExistingOne
                        "
                      >
                        <country-flag
                          :country="
                            getCountryCodeBySrcLang(
                              getPreparedSrcLang(
                                generateSubtitleData.subtitleLabelFromAnotherExistingOne
                              )
                            )
                          "
                        />
                      </template>
                      <template #append>
                        <v-icon> mdi-chevron-down </v-icon>
                      </template>
                    </v-select-legacy>
                  </div>
                </div>
              </div>
            </template>
            <template #step_2>
              <p
                class="text-h7 mt-4 mb-6 pb-1 text-center"
                :class="{ 'neutral--text': !$vuetify.theme.dark }"
              >
                {{
                  $i18next.t('message.ai.subtitles.modal.auto.step_two.title')
                }}
              </p>
              <div style="width: 552px; margin: auto" class="mb-4">
                <v-row no-gutters class="mb-1">
                  <v-col class="d-flex align-center">
                    <v-simple-checkbox
                      :color="!checkboxIndeterminateStatus ? 'primary' : ''"
                      :indeterminate="checkboxIndeterminateStatus"
                      :ripple="false"
                      :value="isAnyAiSubtitleItemSelected"
                      @input="selectAllItems"
                    ></v-simple-checkbox>
                    <span
                      class="subtitle-1"
                      :class="{
                        'neutral--text text--lighten-1': !$vuetify.theme.dark,
                      }"
                      >{{
                        $i18next.t(
                          'message.ai.subtitles.modal.auto.step_two.selectAll'
                        )
                      }}</span
                    >
                  </v-col>
                  <v-col>
                    <v-row no-gutters justify="end"
                      ><div style="width: 181px">
                        <v-text-field
                          v-model="
                            generateSubtitleData.searchAvailableLangsFilter
                          "
                          dense
                          outlined
                          class="v-text-field__custom-colorized"
                          height="32px"
                          hide-details
                          :placeholder="
                            $i18next.t(
                              'message.ai.subtitles.modal.auto.step_two.search'
                            )
                          "
                          prepend-inner-icon="mdi-magnify"
                        >
                        </v-text-field></div
                    ></v-row>
                  </v-col>
                </v-row>
                <div class="container__upload-list--modal">
                  <div
                    class="d-flex"
                    v-for="(item, index) of filteredAiSubtitleOptions"
                    :key="`filteredList_${item.country}_${index}`"
                  >
                    <v-simple-checkbox
                      color="primary"
                      :ripple="false"
                      :value="checkIfSelected(item)"
                      @input="checkboxSelection(item)"
                    ></v-simple-checkbox>
                    <div
                      class="d-flex py-3 align-center f-width"
                      style="border-bottom: 1px solid var(--v-neutral-lighten3)"
                    >
                      <country-flag
                        :country="item.country"
                        class="list-item__flag--filtered"
                      ></country-flag>
                      <span
                        class="subtitle-1 font-weight-medium mr-6"
                        :class="{
                          'neutral--text text--lighten-1': !$vuetify.theme.dark,
                        }"
                        style="
                          white-space: nowrap;
                          margin-left: 10px;
                          width: 140px;
                        "
                        >{{ item.label }}</span
                      >
                      <div class="d-flex justify-space-between f-width">
                        <template
                          v-if="generateSubtitleData.subtitleFromAiOption === 0"
                        >
                          <template v-if="canShowRowValues(item)">
                            <!-- <span
                              class="text-subtitle-2 text-subtitle-2__standardized--modal font-weight-regular"
                              :class="{
                                'neutral--text text--lighten-1':
                                  !$vuetify.theme.dark,
                              }"
                            >
                              {{ getMultiplierAmount(item) }}
                              {{
                                $i18next.t(
                                  'message.ai.subtitles.modal.auto.step_two.credits'
                                )
                              }}/min
                            </span>
                            <span
                              class="text-subtitle-2 text-subtitle-2__standardized--modal font-weight-regular"
                              :class="{
                                'neutral--text text--lighten-1':
                                  !$vuetify.theme.dark,
                              }"
                              >{{ getItemCost(item) }}
                              {{
                                $i18next.t(
                                  'message.ai.subtitles.modal.auto.step_two.credits'
                                )
                              }}</span
                            > -->
                          </template>
                        </template>
                        <template v-else>
                          <!-- <span
                            class="text-subtitle-2 text-subtitle-2__standardized--modal font-weight-regular"
                            :class="{
                              'neutral--text text--lighten-1':
                                !$vuetify.theme.dark,
                            }"
                          >
                            {{ getMultiplierAmount(item) }}&nbsp;{{
                              $i18next.t(
                                'message.ai.subtitles.modal.auto.step_two.credits'
                              )
                            }}/min</span
                          >
                          <span
                            class="text-subtitle-2 text-subtitle-2__standardized--modal font-weight-regular"
                            :class="{
                              'neutral--text text--lighten-1':
                                !$vuetify.theme.dark,
                            }"
                            >{{ getItemCost(item) }}
                            {{
                              $i18next.t(
                                'message.ai.subtitles.modal.auto.step_two.credits'
                              )
                            }}</span
                          > -->
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <template #finalScreen>
              <div
                class="d-flex align-center text-center flex-column"
                style="width: 410px; margin: auto"
              >
                <img
                  :src="require(`@/assets/generatingAiSubtitle.svg`)"
                  class="mt-12 pt-12"
                />
                <span
                  class="text-h6 font-weight-medium mt-7 mb-2"
                  :class="{
                    'brand--text text--darken-1': !$vuetify.theme.dark,
                  }"
                  >{{
                    $i18next.t(
                      'message.ai.subtitles.modal.auto.final_screen.title'
                    )
                  }}</span
                >
                <span
                  class="subtitle-1"
                  :class="{
                    'neutral--text': !$vuetify.theme.dark,
                  }"
                  >{{
                    $i18next.t(
                      'message.ai.subtitles.modal.auto.final_screen.subtitle'
                    )
                  }}</span
                >
                <BaseBtn class="mt-8" @click="handleClose">{{
                  $i18next.t(
                    'message.ai.dubbings.modal.auto.final_screen.actionBtn'
                  )
                }}</BaseBtn>
              </div>
            </template>
          </StepStage>
        </template>
        <template v-else-if="currentModalStage.globalStage === 'upload'">
          <UploadStage
            :loadings="loadings"
            :unusedLangCodes="unusedLangCodes"
            :subStage="currentModalStage.subStage"
            :subtitle="
              $i18next.t('message.ai.subtitles.modal.upload.step_one.title')
            "
            @changeSubStage="updateSubStage"
          />
        </template>
      </v-container>
    </template>
    <template #card-actions>
      <template>
        <v-row
          flat
          dense
          no-gutters
          class="ma-auto"
          :class="{
            'card-height-step3': isStepThree,
          }"
        >
          <v-card
            flat
            tile
            width="800"
            :class="{
              'bg-color card-height': isStepTwo && !isGlobalStateUpload,
            }"
          >
            <v-row
              v-if="
                !errorSituation && isStepTwo && !isGlobalStateUpload && errors
              "
              align="center"
              justify="center"
            >
              <v-col cols="auto">
                <v-card
                  flat
                  tile
                  height="50"
                  width="auto"
                  class="d-flex align-center bg-color flex-column"
                >
                  <div
                    v-if="isAnyAiSubtitleItemSelected"
                    class="d-flex flex-row align-center align-items-center"
                  >
                    <div
                      v-if="!hasAiSubtitle"
                      class="d-flex flex-row align-center align-items-center"
                    >
                      <div class="text-center d-flex flex-column align-center">
                        <div
                          class="d-flex justify-center align-center flex-wrap"
                        >
                          <span
                            v-if="!isBatch"
                            class="mr-2"
                            :class="{
                              'text-neutral neutral--text':
                                !$vuetify.theme.dark,
                            }"
                            >{{
                              $t('message.ai.aiWorkFlowChargeModal.aiCost')
                            }}</span
                          >

                          <span
                            v-else
                            class="mb-0 mr-2"
                            :class="{
                              'text-neutral neutral--text':
                                !$vuetify.theme.dark,
                            }"
                          >
                            {{
                              $t(
                                'message.ai.aiWorkFlowChargeModal.batchAiCost',
                                {
                                  count: batchAllVideosCount,
                                  nounNumber: nounNumber,
                                }
                              )
                            }}
                          </span>

                          <v-img
                            src="@/assets/credit_small_icon.svg"
                            class="mr-2"
                            width="18"
                            height="18"
                            style="max-height: 15px !important"
                          />

                          <span
                            class="text-body-1 line-height--fixed"
                            :class="{
                              'neutral--text': !$vuetify.theme.dark,
                            }"
                          >
                            {{ generatedSubtitleTotalPrice }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div v-else class="d-flex flex-row">
                      <span
                        class="mr-2"
                        :class="{
                          'text-neutral neutral--text': !$vuetify.theme.dark,
                        }"
                        >{{ $t('message.ai.aiWorkFlowChargeModal.aiCost') }}
                      </span>
                      <v-img
                        src="@/assets/credit_small_icon.svg"
                        class="mr-2"
                        width="18"
                        height="18"
                        style="max-height: 15px !important"
                      />

                      <span
                        class="text-body-1 line-height--fixed"
                        :class="{
                          'neutral--text': !$vuetify.theme.dark,
                        }"
                      >
                        {{ generatedSubtitleTotalPrice }}
                      </span>
                    </div>
                  </div>
                  <div
                    v-else-if="!isGlobalStateUpload"
                    class="d-flex flex-row align-center align-items-center"
                  >
                    <span
                      class="mr-2"
                      :class="{
                        'text-neutral neutral--text': !$vuetify.theme.dark,
                      }"
                      >{{ $t('message.ai.noSelectedOption') }}</span
                    >
                  </div>
                  <small>
                    ({{ $t('message.ai.aiWorkFlowChargeModal.promo') }})
                  </small>
                </v-card>
              </v-col>
            </v-row>

            <empty-state
              v-if="isStepTwo && !isGlobalStateUpload && !hasEnoughCredits"
              :logoConfig="logoConfig"
              :arrowConfig="arrowConfig"
              :textConfig="textConfig"
            />
            <template v-else>
              <v-row dense no-gutters>
                <div>
                  <div
                    class="d-flex justify-end f-width py-2 pl-8"
                    v-if="errors.generate"
                  >
                    <span class="red--text text-center">{{
                      errors.generate
                    }}</span>
                  </div>
                  <v-card
                    flat
                    tile
                    width="800"
                    class="pa-0"
                    :class="{
                      'v-card__actions--color': isActionButtonsHighlighted,
                      'v-card__actions--bot-position': !isStepThree,
                      'margin-stp3': isStepThree,
                    }"
                    v-if="
                      currentModalStage.globalStage !== 'initial' &&
                      !(
                        currentModalStage.globalStage === 'generate' &&
                        currentModalStage.subStage === 3
                      )
                    "
                  >
                    <template v-if="isActionButtonsHighlighted">
                      <div>
                        <v-col cols="12" class="credit-spend-col pa-0">
                          <v-card
                            flat
                            class="bg-color bg-color-icon credit-spend-card"
                            height="95px"
                          >
                            <v-spacer></v-spacer>
                            <CreditSpendCard
                              v-if="!errorSituation"
                              :amount="creditAmount"
                              :spendPrice="generatedSubtitleTotalPrice"
                              :subCalc="creditSubRemainCalc"
                              parentModalKey="SubtitleModal"
                              :class="{
                                'hide-card': !isAnyAiSubtitleItemSelected,
                              }"
                            />
                          </v-card>
                          <v-card
                            flat
                            :class="{
                              'v-card__actions--color':
                                isActionButtonsHighlighted,
                            }"
                          >
                            <div class="d-flex justify-center">
                              <BaseBtn
                                v-if="
                                  !forwardBtnData.generationCompleted &&
                                  !isStepThree
                                "
                                class="mx-2"
                                type="secondary"
                                @click="handleGoBack"
                                :disabled="
                                  isAnyLoadingRunning ||
                                  forwardBtnData.isBatchGeneration
                                "
                                style="width: 100px"
                                >{{
                                  $i18next.t('message.pages.video.back')
                                }}</BaseBtn
                              >
                              <template v-if="forwardBtnData.condition">
                                <v-tooltip v-if="forwardBtnData.tooltip" top>
                                  <template v-slot:activator="{ on, attrs }">
                                    <BaseBtn
                                      :class="{ 'btn-width': !isStepTwo }"
                                      class="mx-2"
                                      v-bind="attrs"
                                      v-on="{ ...on, ...$listeners }"
                                      :disabled="forwardBtnData.isDisabled"
                                      :loading="forwardBtnData.isLoading"
                                      @click="forwardBtnData.onClick"
                                    >
                                      {{ forwardBtnData.label }}
                                    </BaseBtn>
                                  </template>
                                  <span>{{ forwardBtnData.tooltip }}</span>
                                </v-tooltip>

                                <BaseBtn
                                  v-else
                                  :class="{ 'btn-width': !isStepTwo }"
                                  class="mx-2 align-center"
                                  :disabled="forwardBtnData.isDisabled"
                                  :loading="forwardBtnData.isLoading"
                                  @click="forwardBtnData.onClick"
                                >
                                  {{ forwardBtnData.label }}
                                </BaseBtn>
                              </template>
                            </div>
                          </v-card>
                        </v-col>
                      </div>
                    </template>

                    <v-col cols="12" class="pa-0 mb-4">
                      <v-card
                        v-if="
                          !isStepTwo ||
                          currentModalStage.globalStage === 'upload'
                        "
                        flat
                        :class="{
                          'v-card__actions--color': isActionButtonsHighlighted,
                        }"
                      >
                        <div class="d-flex justify-center">
                          <BaseBtn
                            v-if="
                              !forwardBtnData.generationCompleted &&
                              !isStepThree
                            "
                            class="mx-2"
                            type="secondary"
                            @click="handleGoBack"
                            :disabled="
                              isAnyLoadingRunning ||
                              forwardBtnData.isBatchGeneration
                            "
                            style="width: 100px"
                            >{{
                              $i18next.t('message.pages.video.back')
                            }}</BaseBtn
                          >
                          <template v-if="forwardBtnData.condition">
                            <v-tooltip v-if="forwardBtnData.tooltip" top>
                              <template v-slot:activator="{ on, attrs }">
                                <BaseBtn
                                  :class="{ 'btn-width': !isStepTwo }"
                                  class="mx-2"
                                  v-bind="attrs"
                                  v-on="{ ...on, ...$listeners }"
                                  :disabled="forwardBtnData.isDisabled"
                                  :loading="forwardBtnData.isLoading"
                                  @click="forwardBtnData.onClick"
                                >
                                  {{ forwardBtnData.label }}
                                </BaseBtn>
                              </template>
                              <span>{{ forwardBtnData.tooltip }}</span>
                            </v-tooltip>

                            <BaseBtn
                              v-else
                              :class="{ 'btn-width': !isStepTwo }"
                              class="mx-2 align-center"
                              :disabled="forwardBtnData.isDisabled"
                              :loading="forwardBtnData.isLoading"
                              @click="forwardBtnData.onClick"
                            >
                              {{ forwardBtnData.label }}
                            </BaseBtn>
                          </template>
                        </div>
                      </v-card>
                    </v-col>
                  </v-card>
                </div>
              </v-row>
            </template>
          </v-card>
        </v-row>
      </template>
    </template>
  </BaseModal>
</template>

<script>
import BaseBtn from '@/components/BaseBtn.vue'
import BaseModal from '@/components/BaseModal.vue'
import CreditSpendCard from '../CreditSpendCard.vue'
import DefaultInitialState from './DefaultInitialStage.vue'
import StepStage from './StepStage.vue'
import i18n from '@/translations/lang'
import UploadStage from './UploadStage.vue'
import CountryFlag from 'vue-country-flag'
import videosApi from '@/services/videosApi'
import aiworkflowApi from '@/services/aiworkflowApi'
import creditsApi from '@/services/creditsApi'
import aiWorkflowMixin from '@/utils/aiWorkflow.mixin'
import { mapState, mapMutations } from 'vuex'
import _ from 'lodash'
import formatTimeMixin from '@/utils/formatTime.mixins.js'
import emptyState from '@/components/emptyState.vue'
import batchAiGenerationSubtitleModal from '@/utils/batchAiGenerationSubtitleModal.mixin.js'

export default {
  name: 'AddSubtitleModal',
  mixins: [aiWorkflowMixin, formatTimeMixin, batchAiGenerationSubtitleModal],
  components: {
    BaseBtn,
    CountryFlag,
    BaseModal,
    DefaultInitialState,
    UploadStage,
    StepStage,
    CreditSpendCard,
    emptyState,
  },
  data: () => ({
    generateSubtitleData: {
      subtitleSrcLangFromVideo: '',
      subtitleLabelFromAnotherExistingOne: '',
      searchAvailableLangsFilter: '',
      subtitleFromAiOption: 0,
      selectedItems: [],
      context: '',
    },
    videoTime: '',
    creditAmount: 0,
    generateSubtitleDataInitial: {},
    subtitles: [],
    currentModalStage: {
      globalStage: 'initial', // initial, upload, generate
      subStage: 1,
    },
    errors: {},
    errorSituation: false,
    loadings: {
      subtitle: false,
      generateSubtitle: false,
    },
    videoId: '',
    subtitleAiPrice: 1, // promotional - was 3
  }),
  computed: {
    ...mapState({
      video: (state) => state.video.video,
      subtitleUploadData: (state) => state.video.subtitleData.upload,
    }),
    ...mapState('user', {
      planInfo: 'userPlanInfo',
    }),
    formatVideoTime() {
      return this.formatTime(this.video.length)
    },
    currentChosenOption() {
      if (this.generateSubtitleData.subtitleFromAiOption === 0)
        return this.generateSubtitleData.subtitleSrcLangFromVideo
      return this.generateSubtitleData.subtitleLabelFromAnotherExistingOne
    },
    initialStageCardsData() {
      return [
        {
          title: this.$i18next.t('message.ai.auto'),
          description: this.$i18next.t(
            'message.ai.subtitles.modal.auto.description'
          ),
          actionBtnText: this.$i18next.t(
            'message.ai.subtitles.modal.auto.modalAction'
          ),
          action: this.goToGenerateStage,
          isBeta: true,
          isAi: true,
          imgSource: require('@/assets/robot.svg'),
        },
        {
          title: this.$i18next.t('message.ai.upload'),
          description: this.$i18next.t(
            'message.ai.subtitles.modal.upload.description'
          ),
          actionBtnText: this.$i18next.t(
            'message.ai.subtitles.modal.upload.modalAction'
          ),
          action: this.goToUploadStage,
          isAi: false,
          imgSource: require('@/assets/upload.svg'),
        },
      ]
    },
    isAnyLoadingRunning() {
      return Boolean(
        Object.values(this.loadings).filter((value) => value).length
      )
    },
    selectedItemsToBeUploaded() {
      return this.generateSubtitleData.selectedItems.filter(
        (item) => !this.checkIfListItemIsSource(item)
      )
    },
    isSourceItemSelected() {
      return this.generateSubtitleData.selectedItems.some((item) =>
        this.checkIfListItemIsSource(item)
      )
    },
    generatedSubtitleTotalPrice() {
      if (this.isBatch) {
        return this.batchTotalPrice
      }
      const length = this.video.length
      const minutes = Math.floor(length / 60)
      const seconds = Math.floor(length % 60)
      const subs_to_queue = this.generateSubtitleData.selectedItems.length
      if (!subs_to_queue) return 0
      const credits_per_video = this.applyCreditsCalcRule({ minutes, seconds })
      const final_price = subs_to_queue * credits_per_video

      return final_price
    },
    creditSubRemainCalc() {
      return this.creditAmount - this.generatedSubtitleTotalPrice
    },
    isGenerateSubtitleBtnDisabled() {
      let condition = true
      if (this.currentModalStage.subStage === 1) {
        if (
          this.generateSubtitleData.subtitleFromAiOption === 0 &&
          !this.generateSubtitleData.subtitleSrcLangFromVideo
        )
          condition = false
        if (
          this.generateSubtitleData.subtitleFromAiOption === 1 &&
          !this.generateSubtitleData.subtitleLabelFromAnotherExistingOne
        )
          condition = false
        if (!this.unusedSubLangCodesAi.length) condition = false
      }
      return this.loadings.generateSubtitle || !condition
    },
    forwardBtnData() {
      const data = {
        isDisabled: false,
        isLoading: false,
        onClick: () => {},
        tooltip: '',
        label: '',
        condition: true,
        generationCompleted: false,
        isBatchGeneration: false,
      }

      const { globalStage, subStage } = this.currentModalStage

      data.onClick = this.handleNextStep

      if (globalStage === 'upload') {
        data.isLoading = this.loadings.subtitle
        data.label = this.$i18next.t(
          'message.ai.subtitles.modal.upload.step_two.actionBtn'
        )
        if (subStage === 1) {
          data.isDisabled = true
        } else {
          data.isDisabled = this.loadings.subtitle || this.hasDuplicatedLangs
        }
        data.tooltip = this.hasDuplicatedLangsTooltip
      }

      if (globalStage === 'generate') {
        data.isLoading = this.loadings.generateSubtitle
        data.isDisabled = this.isGenerateSubtitleBtnDisabled
        data.label = this.$i18next.t(
          'message.ai.subtitles.modal.auto.step_one.actionBtn'
        )
        if (subStage === 1) {
          data.tooltip = !this.unusedSubLangCodesAi.length
            ? this.$i18next.t(
                'message.ai.subtitles.modal.auto.step_one.placeholder'
              )
            : false
          if (this.isBatch) {
            data.isBatchGeneration = true
            data.isLoading = this.loadingConfigs
            if (this.loadingConfigs) {
              data.tooltip = this.$i18next.t(
                'message.ai.subtitles.modal.auto.step_one.gettingReady'
              )
            }
          }
        }
        if (subStage === 2) {
          if (this.generateSubtitleData.subtitleFromAiOption === 0) {
            const checkFinalOptionsSize =
              this.generateSubtitleData.selectedItems.length
            const checkIfSourceItemIsSelected = this.isSourceItemSelected
            data.isDisabled =
              !checkFinalOptionsSize ||
              (this.sourceItem.srclang !== 'auto' &&
                !checkIfSourceItemIsSelected)

            if (!checkFinalOptionsSize) {
              data.tooltip = this.$i18next.t(
                'message.ai.subtitles.modal.auto.step_two.tooltips.quantity'
              )
            }
            if (
              this.sourceItem.srclang !== 'auto' &&
              !checkIfSourceItemIsSelected
            ) {
              data.tooltip = this.$i18next.t(
                'message.ai.subtitles.modal.auto.step_two.tooltips.source',
                { lang: this.sourceItem.label }
              )
            }
            if (this.isBatch && checkFinalOptionsSize) {
              const hasPossibleDubs = this.handleExistingSubs
              data.isDisabled = hasPossibleDubs
              if (hasPossibleDubs) {
                data.tooltip = this.$i18next.t(
                  'message.ai.subtitles.modal.auto.step_three.subsAlreadyExists'
                )
              }
            }
          } else {
            const checkFinalOptionsSize =
              this.generateSubtitleData.selectedItems.length
            data.isDisabled = !checkFinalOptionsSize

            if (!checkFinalOptionsSize) {
              data.tooltip = this.$i18next.t(
                'message.ai.subtitles.modal.auto.step_two.tooltips.quantity'
              )
            }
          }

          if (this.creditSubRemainCalc < 0) {
            data.isDisabled = this.creditSubRemainCalc < 0
            data.tooltip = this.$i18next.t('message.ai.notEnoughCredits')
          }
        }
        if (subStage === 2) {
          data.label = this.$i18next.t(
            'message.ai.subtitles.modal.auto.step_three.actionBtn'
          )
          if (this.isBatch) {
            data.onClick = this.handleBatchSubGeneration
          } else {
            data.onClick = this.handlePostAiGeneratedSubtitle
          }
        }
        if (subStage === 3) {
          data.condition = false
        }
        if (subStage > 3 && this.isBatch) {
          data.condition = false
          data.generationCompleted = true
        }
      }

      return data
    },
    isActionButtonsHighlighted() {
      if (
        this.currentModalStage.globalStage === 'generate' &&
        this.currentModalStage.subStage === 2
      )
        return true
      return false
    },
    disableDefaultPadding() {
      if (this.currentModalStage.globalStage !== 'initial') return true
      return false
    },
    checkboxIndeterminateStatus() {
      return (
        this.generateSubtitleData.selectedItems.length > 0 &&
        this.generateSubtitleData.selectedItems.length <
          this.filteredAiSubtitleOptions.length
      )
    },
    isAnyAiSubtitleItemSelected() {
      return this.generateSubtitleData.selectedItems.length > 0
    },
    allAvailableAiSubtitleOptions() {
      const filteredSubtitleOptions = this.unusedLangCodes.filter(
        ({ label }) => {
          let compareLabel = ''
          if (
            this.generateSubtitleData.subtitleSrcLangFromVideo ||
            this.generateSubtitleData.subtitleLabelFromAnotherExistingOne
          ) {
            compareLabel =
              this.generateSubtitleData.subtitleFromAiOption === 0
                ? this.generateSubtitleData.subtitleSrcLangFromVideo
                : this.generateSubtitleData.subtitleLabelFromAnotherExistingOne
          }
          const isAllowed = label !== compareLabel
          return isAllowed
        }
      )
      return filteredSubtitleOptions
    },
    filteredAiSubtitleOptions() {
      let filteredSubtitleOptions = this.unusedSubLangCodesAi.filter(
        ({ label }) => {
          return this.checkSearch(label)
        }
      )
      if (this.isBatch) {
        filteredSubtitleOptions = this.handleBatchSubtitleOptions()
      }

      return filteredSubtitleOptions.sort((a, b) => {
        if (
          this.checkIfListItemIsSource(a) &&
          !this.checkIfListItemIsSource(b)
        ) {
          return -1
        } else if (
          !this.checkIfListItemIsSource(a) &&
          this.checkIfListItemIsSource(b)
        ) {
          return 1
        }
        return 0
      })
    },
    disabledSendButton() {
      return this.loadings.subtitle || this.hasDuplicatedLangs
    },
    hasDuplicatedLangsTooltip() {
      if (this.hasDuplicatedLangs)
        return this.$i18next.t(
          'message.ai.subtitles.modal.upload.notDuplicatedLangs'
        )
      return false
    },
    hasDuplicatedLangs() {
      const subtitleLabels =
        this.subtitleUploadData.subtitleListToBeUploaded.map(
          (item) => item.label
        )
      return this.containsDuplicates(subtitleLabels)
    },
    unusedLangCodes() {
      return this.getUnusedSubtitleItems(this.langCodes)
    },
    addSubtitleFileErrorMsg() {
      if (this.errors.wrongSubtitleFile) {
        return this.$i18next.t(
          'message.pages.videoEditModal.errors.wrongSubtitleFile'
        )
      }
      return ''
    },
    logoConfig() {
      return {
        top: -83,
        left: 295,
        size: 0.5,
        rotation: 0,
      }
    },
    arrowConfig() {
      return {
        top: -50,
        left: 100,
        size: 0.65,
        rotation: 100,
      }
    },
    textConfig() {
      return {
        text: this.$i18next.t('message.ai.aiWorkFlowChargeModal.emptyState'),
        top: 70,
        left: 235,
        size: '1em',
        rotation: 0,
        textAlign: 'center',
        textWidth: '40%',
        hrefText: this.$i18next.t(
          'message.ai.aiWorkFlowChargeModal.addCredits'
        ),
        onClick: this.handleGoChargeCredits,
      }
    },
    hasEnoughCredits() {
      const subPrice = this.calcPrice()
      const hasEnoughCredits = this.calcEnoughCredits(subPrice)
      return !(hasEnoughCredits < 0)
    },
    isStepTwo() {
      return this.currentModalStage.subStage === 2
    },
    isStepThree() {
      return this.currentModalStage.subStage === 3
    },
    hasAiSubtitle() {
      return this.generateSubtitleData.subtitleFromAiOption === 1
    },
    isGlobalStateUpload() {
      return this.currentModalStage.globalStage === 'upload'
    },
    nounNumber() {
      if (this.batchAllVideosCount > 1) return 'videos'
      return 'video'
    },
  },
  watch: {
    open(val) {
      if (!val) this.handleClose()
    },
    subtitles() {
      this.checkSubtitleOption()
    },
  },
  methods: {
    ...mapMutations('video', ['UPDATE_VIDEO']),
    getItemCost(item) {
      let videoTime = Math.floor(this.video.length / 60)
      if (!videoTime) videoTime = 1
      const calc = this.getMultiplierAmount(item) * videoTime
      return calc ? calc : 1
    },
    updateSubStage(subStage) {
      this.currentModalStage.subStage = subStage
    },
    getMultiplierAmount(item) {
      if (this.sourceItem.srclang !== 'auto') {
        if (this.checkIfListItemIsSource(item)) {
          return 3 + 1
        }
        return 1
      }
      if (
        this.generateSubtitleData.selectedItems.length &&
        this.generateSubtitleData.selectedItems[0].srclang === item.srclang
      ) {
        return 1 + 3
      }
      return 1
    },
    canShowRowValues(item) {
      let isSourceItemSelected = this.isSourceItemSelected
      if (this.sourceItem.srclang === 'auto') {
        isSourceItemSelected = Boolean(
          this.generateSubtitleData.selectedItems.length
        )
      }

      return (
        (this.checkIfListItemIsSource(item) && isSourceItemSelected) ||
        (!this.checkIfListItemIsSource(item) &&
          isSourceItemSelected &&
          this.checkIfItemIsSelected(item))
      )
    },
    checkIfItemIsSelected({ srclang }) {
      return this.generateSubtitleData.selectedItems.some(
        (item) => item.srclang === srclang
      )
    },
    checkSubtitleOption() {
      if (this.subtitles?.length) {
        this.generateSubtitleData.subtitleFromAiOption = 1
      } else {
        this.generateSubtitleData.subtitleFromAiOption = 0
      }
    },
    async getCreditsInfo() {
      try {
        const { data } = await creditsApi.getCreditsBalance()
        this.creditAmount = data.balance
      } catch (error) {
        console.error('error', error)
      }
    },
    onOpenHook({ videoId, initialState, isBatch, selectedLang }) {
      this.getCreditsInfo()
      if (isBatch) {
        this.isBatch = isBatch
        this.handleOpenBatchSubGenerationModal()
        return
      } else {
        this.isBatch = false
      }
      this.generateSubtitleDataInitial = _.cloneDeep(this.generateSubtitleData)
      this.videoId = videoId
      this.open = true
      this.subtitles = this.$store.state.video.video.config?.subtitles || []

      if (this.video.original_lang) {
        this.generateSubtitleData.subtitleSrcLangFromVideo =
          this.video.original_lang
      } else if (this.planInfo.currency) {
        if (this.planInfo.currency === 'brl') {
          this.generateSubtitleData.subtitleSrcLangFromVideo = 'pt-BR'
        } else {
          this.generateSubtitleData.subtitleSrcLangFromVideo = 'en'
        }
      } else {
        this.generateSubtitleData.subtitleSrcLangFromVideo = 'auto'
      }

      if (this.subtitles?.length) {
        this.generateSubtitleData.subtitleLabelFromAnotherExistingOne =
          this.subtitles[0].srclang
      }

      if (initialState) {
        this.handleUpdateModalStage({
          globalStage: 'generate',
          subStage: 1,
        })
      }
      if (selectedLang) {
        this.checkIfSelected(selectedLang)
        this.checkboxSelection(selectedLang)
      }
      this.checkSubtitleOption()
    },
    selectAllItems() {
      let arr = []

      if (
        this.generateSubtitleData.selectedItems.length !==
        this.filteredAiSubtitleOptions.length
      ) {
        arr = this.filteredAiSubtitleOptions
      }

      this.generateSubtitleData.selectedItems = arr
    },
    checkboxSelection(item) {
      let alreadySelected = this.checkIfSelected(item)
      if (alreadySelected) {
        let newArr = [
          ...this.generateSubtitleData.selectedItems.filter((lookItem) => {
            return lookItem.srclang !== item.srclang
          }),
        ]
        this.generateSubtitleData.selectedItems = [...newArr]
      } else {
        this.generateSubtitleData.selectedItems.push(item)
      }
    },
    checkIfSelected(item) {
      let foundItem = this.generateSubtitleData.selectedItems.find(
        (searchedItem) => {
          return searchedItem.srclang === item.srclang
        }
      )
      if (foundItem) return true
      return false
    },
    checkSearch(label) {
      if (this.generateSubtitleData.searchAvailableLangsFilter) {
        return this.areStringsEqualIgnoringCaseAndSymbols(
          this.generateSubtitleData.searchAvailableLangsFilter,
          label
        )
      }
      return true
    },
    handleGoBack() {
      if (this.currentModalStage.subStage === 3) {
        this.subsAlreadyExists = false
      }

      this.clearErrors()
      if (this.currentModalStage.subStage > 1) {
        if (
          this.currentModalStage.globalStage === 'generate' &&
          this.currentModalStage.subStage === 2
        )
          this.generateSubtitleData.selectedItems = []
        return (this.currentModalStage.subStage -= 1)
      }
      this.goToInitial()
    },
    getLangCodeByLabel(incomingLabel) {
      return this.langCodes.filter(({ label }) => label === incomingLabel)[0]
    },
    handleNextStep() {
      if (
        this.currentModalStage.globalStage === 'upload' &&
        this.currentModalStage.subStage === 2
      )
        return this.handlePostNewSubtitles()
      if (this.currentModalStage.globalStage === 'generate') {
        if (
          this.currentModalStage.subStage === 1 &&
          !this.generateSubtitleData.subtitleFromAiOption
        ) {
          if (this.currentChosenOption !== 'auto') {
            this.sourceItem = this.getLangCodeBySrcLang(
              this.currentChosenOption
            )
            this.generateSubtitleData.selectedItems.push(this.sourceItem)
          } else {
            this.sourceItem = this.availableTranscriptionLangCodes[0]
          }
        }
        if (this.currentModalStage.subStage === 3) return this.handleClose()
        if (this.currentModalStage.subStage === 2) {
          return this.handlePostAiGeneratedSubtitle()
        } else {
          this.currentModalStage.subStage += 1
        }
      }
    },
    async handlePostAiGeneratedSubtitle() {
      this.loadings.generateSubtitle = true
      let data = {}
      try {
        if (!this.generateSubtitleData.subtitleFromAiOption) {
          const additionalLangs = this.generateSubtitleData.selectedItems
            .filter(
              (item) =>
                item.srclang !==
                this.generateSubtitleData.subtitleSrcLangFromVideo
            )
            .map((item) => ({
              lang: this.getPreparedSrcLang(item.srclang),
            }))
          data = await aiworkflowApi.createTranscription({
            fromLang: this.getPreparedSrcLang(
              this.generateSubtitleData.subtitleSrcLangFromVideo
            ),
            videoId: this.videoId,
            additionalLangs,
            ctx: this.generateSubtitleData.context,
          })
          const batchMetadata = data.data
          this.$root.$emit('batchGenerationMetadata', batchMetadata)

          if (
            this.generateSubtitleData.subtitleSrcLangFromVideo &&
            this.generateSubtitleData.subtitleSrcLangFromVideo !== 'auto'
          ) {
            this.UPDATE_VIDEO_ORIGINAL_LANG(
              this.generateSubtitleData.subtitleSrcLangFromVideo
            )
            this.$root.$emit('updateVideoInfo', this.video)
          }
          this.$root.$emit('addAiWorkflowSubtitle', data.data)
        } else {
          const finishedPromises = []

          for (const item of this.selectedItemsToBeUploaded) {
            const response = await aiworkflowApi.createTranslation({
              fromLang: this.getPreparedSrcLang(
                this.generateSubtitleData.subtitleLabelFromAnotherExistingOne
              ),
              videoId: this.videoId,
              toLang: item.srclang,
            })
            finishedPromises.push(response)
          }
          const _this = this
          finishedPromises.map((item) => {
            _this.$root.$emit('addAiWorkflowSubtitle', item.data)
            const batchMetadata = item.data
            _this.$root.$emit('batchGenerationMetadata', batchMetadata)
          })
        }
        this.clearErrors()
        this.currentModalStage.subStage += 1
      } catch (err) {
        if (this.isBatch) {
          this.currentModalStage.subStage = 3
        } else {
          console.error(err)
          console.error({ ...err })
          if (
            err?.response?.data &&
            err?.response?.data?.errMsg &&
            err?.response?.data?.errCode
          ) {
            this.errorSituation = true
            console.error(err.response.data.errMsg)
            const badRequestAllowedValues =
              'should be equal to one of the allowed values: en, en-US, pt, auto, pt-BR, es, es-MX, en-UK, it, it-ch, fr, fr-be, fr-ca, fr-lu, fr-ch, de, de-at, de-li, de-lu, de-ch.'
            this.errors.generate = `${err.response.data.errCode}: ${err.response.data.errMsg}`
            if (err.response.data.errMsg.includes(badRequestAllowedValues)) {
              this.errors.generate = `${i18n.t(
                'message.ai.noLanguageSupport'
              )}: ${i18n.t('message.ai.skippedWarningNotSupported')}`
            }
          } else if (err?.message) {
            console.error(err.message)
            this.errors.generate = err.message
          }
        }
      }
      this.loadings.generateSubtitle = false
      this.$root.$emit('reloadBalance')
      this.$root.$emit('reloadBalanceDrawer')
    },
    async handlePostNewSubtitles() {
      this.loadings.subtitle = true
      for (let item of this.subtitleUploadData.subtitleListToBeUploaded) {
        await this.handleAddSubtitle(item)
      }
      this.loadings.subtitle = false
      this.handleClose()
      this.subtitleUploadData.subtitleListToBeUploaded = []
    },
    async handleAddSubtitle(subtitle) {
      this.clearErrors()
      const data = new FormData()
      const langCode = this.langCodes.filter((lang) => {
        return lang.label === subtitle.label
      })
      subtitle.srclang = langCode[0].srclang
      data.append('file', subtitle.file)
      data.append('label', subtitle.label)
      data.append('srclang', subtitle.srclang)
      try {
        await videosApi.addSubtitleToVideo(this.videoId, data)
        this.loadings.subtitle = false
        this.$store.state.video.video.config?.subtitles.push({
          label: subtitle.label,
          srclang: subtitle.srclang,
        })
      } catch (e) {
        console.error(e)
      }
    },
    clearErrors() {
      Object.keys(this.errors).forEach((e) => (this.errors[e] = false))
    },
    handleClose() {
      this.subsAlreadyExists = false
      this.$root.$emit('closeSubtitleModal')
    },
    onCloseHook() {
      this.generateSubtitleData = _.cloneDeep(this.generateSubtitleDataInitial)
      this.clearErrors()
      this.goToInitial()
    },
    goToGenerateStage() {
      this.handleUpdateModalStage({
        globalStage: 'generate',
        subStage: 1,
      })
    },
    goToUploadStage() {
      this.handleUpdateModalStage({
        globalStage: 'upload',
        subStage: 1,
      })
    },
    goToInitial() {
      this.handleUpdateModalStage()
    },
    handleUpdateModalStage({ globalStage = 'initial', subStage = 1 } = {}) {
      this.currentModalStage.globalStage = globalStage
      this.currentModalStage.subStage = subStage
    },
    handleGoChargeCredits() {
      this.handleClose()
      return this.$router.push({ name: 'Credits', query: { charge: 'true' } })
    },
    calcEnoughCredits(price) {
      return this.creditAmount - price
    },
    calcPrice() {
      if (this.isBatch) {
        return this.batchTotalPrice
      }
      if (this.generateSubtitleData.subtitleFromAiOption) return 0

      let videoTimeMinutes = Math.floor(this.video.length / 60)

      if (!videoTimeMinutes) return (totalPrice = this.subtitleAiPrice)

      let remainingSeconds = this.video.length % 60

      let totalPrice = videoTimeMinutes * this.subtitleAiPrice

      if (remainingSeconds > 0) {
        totalPrice += this.subtitleAiPrice
      }

      return totalPrice
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_ai_works';
</style>

<style lang="scss" scoped>
.v-simple-checkbox {
  i {
    color: var(--v-primary-base) !important;
  }
}
.text-bold {
  font-weight: 500;
  font-size: 16px;
}
.container {
  &__setup-action {
    width: 347px;
    margin: auto;
  }
}
.container__credit-spend {
  width: 400px;
  position: relative;
  left: 0;
  margin: auto;
}
.bg-color {
  background-color: var(--v-newBackground-base) !important;
}
.card-height {
  height: auto !important;
}
.hide-card {
  visibility: hidden;
}
.card-height-step3 {
  max-height: 30px;
}
.btn-width {
  width: 100px;
}
.margin-stp3 {
  margin-top: 11px !important;
}
</style>

<style lang="scss">
.bg-color-icon {
  .v-image__image {
    background-color: var(--v-newBackground-base) !important;
  }
}

@media screen and (max-height: 768px) {
  .v-card__actions--bot-position {
    height: 100%;
    margin-bottom: -3%;
  }

  .credit-spend-col {
    display: flex;
    align-items: center;
  }
}
</style>
